import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export interface DateParseConfig {
  strictMode?: boolean;
  allowedFormats?: string[];
  minYear?: number;
  maxYear?: number;
}

export class DateHelper {
  private static readonly DEFAULT_FORMATS = [
    'YYYY-MM-DD',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DDTHH:mm:ss.SSS',
    'YYYY-MM-DDTHH:mm:ssZ',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'DD/MM/YYYY',
    'DD-MM-YYYY',
    'DD.MM.YYYY',
    'MM/DD/YYYY',
    'MM-DD-YYYY',
    'MM.DD.YYYY',
    'DD/MM/YYYY, HH:mm:ss',
    'MM/DD/YYYY, HH:mm:ss',
    'DD-MM-YYYY, HH:mm:ss',
    'MM-DD-YYYY, HH:mm:ss',
    'DD.MM.YYYY, HH:mm:ss',
    'MM.DD.YYYY, HH:mm:ss',
    'DD MMM YYYY',
    'MMM DD, YYYY',
    'MMMM DD, YYYY'
  ];

  private static readonly DEFAULT_CONFIG: Required<DateParseConfig> = {
    strictMode: false,
    allowedFormats: DateHelper.DEFAULT_FORMATS,
    minYear: 1900,
    maxYear: 2100
  };

  public static parseDateWithTimezone(
    dateString: string | null | undefined,
    timezone: string,
    config: DateParseConfig = {}
  ): dayjs.Dayjs | null {
    if (!dateString) {
      return null;
    }

    const finalConfig = { ...this.DEFAULT_CONFIG, ...config };
    const trimmedValue = String(dateString).trim();

    if (this.isLikelyNotDate(trimmedValue)) {
      return null;
    }

    const parsedDate = this.parseDate(trimmedValue, finalConfig);

    if (!parsedDate || !parsedDate.isValid()) {
      return null;
    }

    if (!this.validateDateRange(parsedDate, finalConfig)) {
      return null;
    }

    return parsedDate.tz(timezone);
  }

  public static isValidDateString(value: string | null | undefined): boolean {
    if (!value) return false;
    return this.parseDateWithTimezone(value, 'UTC') !== null;
  }

  private static parseDate(value: string, config: Required<DateParseConfig>): dayjs.Dayjs | null {
    for (const format of config.allowedFormats) {
      const parsed = dayjs(value, format, config.strictMode);
      if (parsed.isValid()) {
        if (this.isValidParsedDate(parsed, value, format)) {
          return parsed;
        }
      }
    }

    if (/^\d{4}-\d{2}-\d{2}([T\s]\d{2}:\d{2}:\d{2})?/.test(value)) {
      const parsed = dayjs(value);
      if (parsed.isValid()) {
        if (this.isValidBasicDate(parsed, value)) {
          return parsed;
        }
      }
    }

    return null;
  }

  private static isValidBasicDate(parsed: dayjs.Dayjs, originalValue: string): boolean {
    const year = parsed.year();
    const month = parsed.month() + 1;
    const day = parsed.date();

    if (year < 1000 || year > 9999) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    const daysInMonth = parsed.daysInMonth();
    if (day > daysInMonth) return false;

    const match = originalValue.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, origYear, origMonth, origDay] = match;
      if (
        parseInt(origYear) !== year ||
        parseInt(origMonth) !== month ||
        parseInt(origDay) !== day
      ) {
        return false;
      }
    }

    return true;
  }

  private static isValidParsedDate(
    parsed: dayjs.Dayjs,
    originalValue: string,
    format: string
  ): boolean {
    const year = parsed.year();
    const month = parsed.month() + 1;
    const day = parsed.date();

    if (year < 1000 || year > 9999) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    const daysInMonth = parsed.daysInMonth();
    if (day > daysInMonth) return false;

    if (format.includes('DD') && format.includes('MM') && format.includes('YYYY')) {
      const numbers = originalValue.match(/\d+/g);
      if (numbers && numbers.length === 3) {
        const [first, second, third] = numbers.map(n => parseInt(n));

        if (format.startsWith('DD')) {
          if (first !== day || second !== month || third !== year) {
            return false;
          }
        } else if (format.startsWith('MM')) {
          if (first !== month || second !== day || third !== year) {
            return false;
          }
        }
      }
    }

    return true;
  }

  private static isLikelyNotDate(value: string): boolean {
    if (value.length < 4 || value.length > 30) {
      return true;
    }

    if (/^\d+$/.test(value) && value.length > 8) {
      return true;
    }

    if (/[<>{}[\]()@#$%^&*=|\\`~]/.test(value)) {
      return true;
    }

    if (/@/.test(value) && /\.[a-z]{2,}$/i.test(value)) {
      return true;
    }

    if (/^https?:\/\//.test(value)) {
      return true;
    }

    return false;
  }

  private static validateDateRange(date: dayjs.Dayjs, config: Required<DateParseConfig>): boolean {
    const year = date.year();
    return year >= config.minYear && year <= config.maxYear;
  }
}
